import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { MessageCircle, X, Send, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "How do I place an order?",
  "What's in the Keratin Essentials pack?",
  "How do I access training?",
  "What are your delivery times?",
  "How do I become a stockist?",
];

export default function ChatWidget({ raised = false }) {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${user?.user_id || 'anon'}_${Date.now()}`);
  const scrollRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  if (!token) return null;

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;

    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat/message`, { message: msg, session_id: sessionId }, { headers });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: "I apologise, I'm having a brief issue. Please contact us at info@revitalisekeratin.com or call 020 7118 1255." }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-rvk-float="true"
          className={`fixed ${raised ? 'bottom-[88px] xl:bottom-6' : 'bottom-[88px] xl:bottom-6'} right-6 z-[46] w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-105`}
          style={{ background: '#B99A65' }}
          data-testid="chat-widget-btn"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div data-rvk-float="true" className={`fixed ${raised ? 'bottom-[88px] xl:bottom-6' : 'bottom-[88px] xl:bottom-6'} right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] flex flex-col`} style={{ height: '520px', background: '#0A0A0A', borderRadius: '12px', boxShadow: '0 12px 48px rgba(0,0,0,0.4)', border: '1px solid rgba(197,160,89,0.2)' }} data-testid="chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(197,160,89,0.15)' }}>
            <div>
              <p className="text-sm font-semibold text-[#E8E0D4]" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.5px' }}>Revitalise Assistant</p>
              <p className="text-[10px] text-[#B99A65] tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>Ask anything about our products</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#8A8078] hover:text-[#E8E0D4] transition-colors" data-testid="chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollBehavior: 'smooth' }}>
            {messages.length === 0 && (
              <div className="space-y-3 py-2">
                <p className="text-xs text-[#8A8078]" style={{ fontFamily: "'Inter', sans-serif" }}>Suggested questions:</p>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="block w-full text-left text-xs px-3 py-2.5 transition-colors"
                    style={{ background: '#111111', color: '#E8E0D4', borderRadius: '8px', fontFamily: "'Inter', sans-serif", border: '1px solid rgba(197,160,89,0.1)' }}
                    data-testid={`suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="max-w-[85%] px-3.5 py-2.5 text-xs leading-relaxed"
                  style={{
                    borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    background: m.role === 'user' ? '#B99A65' : '#111111',
                    color: m.role === 'user' ? '#0A0A0A' : '#E8E0D4',
                    fontFamily: "'Inter', sans-serif",
                  }}
                  data-testid={`msg-${i}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-3" style={{ background: '#111111', borderRadius: '12px 12px 12px 2px' }}>
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-[#B99A65] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-[#B99A65] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-[#B99A65] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Contact Bar */}
          <div className="flex items-center gap-4 px-4 py-2" style={{ borderTop: '1px solid rgba(255,255,255,0.03)' }}>
            <a href="tel:02071181255" className="flex items-center gap-1 text-[9px] text-[#8A8078] hover:text-[#B99A65] transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
              <Phone className="w-3 h-3" /> 020 7118 1255
            </a>
            <a href="mailto:info@revitalisekeratin.com" className="flex items-center gap-1 text-[9px] text-[#8A8078] hover:text-[#B99A65] transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
              <Mail className="w-3 h-3" /> Email us
            </a>
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-3" style={{ borderTop: '1px solid rgba(197,160,89,0.15)' }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask a question..."
              className="flex-1 text-xs px-3 py-2.5 outline-none"
              style={{ background: '#111111', color: '#E8E0D4', borderRadius: '8px', border: '1px solid rgba(197,160,89,0.1)', fontFamily: "'Inter', sans-serif" }}
              data-testid="chat-input"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="w-9 h-9 flex items-center justify-center rounded-lg transition-all disabled:opacity-30"
              style={{ background: '#B99A65' }}
              data-testid="chat-send"
            >
              <Send className="w-4 h-4 text-[#0A0A0A]" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
